export type Groupable = {
  uid: string
  group_id?: string | null
}

export type SelectionPopoverAction = "group" | "ungroup"

export function groupIdOf(obj: Groupable): string | null {
  const id = obj.group_id
  return typeof id === "string" && id.length > 0 ? id : null
}

export function memberUids(objects: readonly Groupable[], groupId: string): string[] {
  return objects.filter((obj) => groupIdOf(obj) === groupId).map((obj) => obj.uid)
}

/** Selection is every member of one group, and that group has at least two members. */
export function isCompleteGroup(selected: readonly string[], objects: readonly Groupable[]): boolean {
  if (selected.length < 2) return false
  const first = objects.find((obj) => obj.uid === selected[0])
  const groupId = first ? groupIdOf(first) : null
  if (!groupId) return false
  const members = memberUids(objects, groupId)
  if (members.length !== selected.length) return false
  const picked = new Set(selected)
  return members.every((uid) => picked.has(uid))
}

export function selectionPopoverAction(
  selected: readonly string[],
  objects: readonly Groupable[],
): SelectionPopoverAction | null {
  if (isCompleteGroup(selected, objects)) return "ungroup"
  if (selected.length >= 2) return "group"
  return null
}

/** Plain click: a grouped object selects the whole group. */
export function plainClickSelection(objects: readonly Groupable[], uid: string): string[] {
  const obj = objects.find((item) => item.uid === uid)
  if (!obj) return []
  const groupId = groupIdOf(obj)
  if (!groupId) return [uid]
  const members = memberUids(objects, groupId)
  if (members.length < 2) return [uid]
  return members
}

export function toggleSelectionUid(selected: readonly string[], uid: string): string[] {
  if (selected.includes(uid)) return selected.filter((item) => item !== uid)
  return [...selected, uid]
}

/** Shift-click adds or removes the same set a plain click would select. */
export function shiftClickSelection(objects: readonly Groupable[], selected: readonly string[], uid: string): string[] {
  const unit = plainClickSelection(objects, uid)
  if (unit.length === 0) return [...selected]
  const have = new Set(selected)
  if (unit.every((item) => have.has(item))) return selected.filter((item) => !unit.includes(item))
  return [...selected, ...unit.filter((item) => !have.has(item))]
}

function selectionWithGroupPeers(objects: readonly Groupable[], selected: readonly string[]): string[] {
  const pick = new Set(selected)
  for (const uid of selected) {
    for (const member of plainClickSelection(objects, uid)) pick.add(member)
  }
  return [...pick]
}

export function nextGroupId(objects: readonly Groupable[]): string {
  const used = new Set(objects.map((obj) => groupIdOf(obj)).filter((id): id is string => id != null))
  let i = 1
  while (used.has(`grp_${i}`)) i += 1
  return `grp_${i}`
}

function withoutGroupId<T extends Groupable>(obj: T): T {
  if (!groupIdOf(obj)) return obj
  const copy = { ...obj }
  delete copy.group_id
  return copy
}

function dissolveSmallGroups<T extends Groupable>(objects: readonly T[]): T[] {
  const counts = new Map<string, number>()
  for (const obj of objects) {
    const id = groupIdOf(obj)
    if (!id) continue
    counts.set(id, (counts.get(id) ?? 0) + 1)
  }
  return objects.map((obj) => {
    const id = groupIdOf(obj)
    if (!id || (counts.get(id) ?? 0) >= 2) return obj
    return withoutGroupId(obj)
  })
}

export function applyGroup<T extends Groupable>(objects: readonly T[], selected: readonly string[]): T[] {
  if (selected.length < 2) return objects.map((obj) => ({ ...obj }))
  const pick = new Set(selectionWithGroupPeers(objects, selected))
  const groupId = nextGroupId(objects)
  const next = objects.map((obj) => (pick.has(obj.uid) ? { ...obj, group_id: groupId } : { ...obj }))
  return dissolveSmallGroups(next)
}

export function applyUngroup<T extends Groupable>(objects: readonly T[], selected: readonly string[]): T[] {
  const pick = new Set(selected)
  const next = objects.map((obj) => (pick.has(obj.uid) ? withoutGroupId({ ...obj }) : { ...obj }))
  return dissolveSmallGroups(next)
}

/** Other members that should follow a drag. Empty unless the selection is that whole group. */
export function groupPeerUids(
  objects: readonly Groupable[],
  selected: readonly string[],
  draggedUid: string,
): string[] {
  if (!selected.includes(draggedUid) || !isCompleteGroup(selected, objects)) return []
  return selected.filter((uid) => uid !== draggedUid)
}
